import PlayerCharacter from "./PlayerCharacter";
import common from "../common";
import TileManager from "./TileManager";
import EntityManager from "./EntityManager";
import ObstacleManager from "./ObstacleManager";
import DrawingSpine from "../drawing/DrawingSpine";
import DrawingTextureHack from "../drawing/DrawingTextureHack";
import paper from "paper";
import FloorTileManager from "./FloorTileManager";
import InsideDoor from "./InsideDoor";
import {GlowFilter} from "pixi-filters";
import Shake from "../utils/Shake";
import {Emitter} from "pixi-particles";

export default class World extends PIXI.Container {

    get moveTarget() {
        return this._moveTarget;
    }

    set moveTarget(value) {
        if (this._running)
            this._moveTarget = value;
    }

    constructor() {
        super();

        this.signals = {};
        this.signals.began = new P3.utils.MiniSignal();
        this.signals.completed = new P3.utils.MiniSignal();

        this._tweens = new P3.utils.TWEEN.Group();

        this._running = false;

        this._worldLength = 12000;

        this._minHeight = -140;
        this._maxHeight = -10;

        this._floorHolder = new PIXI.Container();
        this.addChild(this._floorHolder);

        this._tileHolder = new PIXI.Container();
        this.addChild(this._tileHolder);

        this._wallEntityHolder = new PIXI.Container();
        this.addChild(this._wallEntityHolder);

        this._backHolder = new PIXI.Container();
        this.addChild(this._backHolder);

        this._playerHolder = new PIXI.Container();
        this.addChild(this._playerHolder);

        this._obstacleHolder = new PIXI.Container();
        this._playerHolder.addChild(this._obstacleHolder);

        this._frontHolder = new PIXI.Container();
        this.addChild(this._frontHolder);

        this._coffin = new PIXI.spine.Spine(P3.assets.spineData("coffin"));
        this._coffin.state.setAnimation(0, "idle_generic", false);
        this._coffin.update(0);
        this._coffin.position.set(common.STAGE_WIDTH * 0.5 - 400, -80);
        this._playerHolder.addChild(this._coffin);

        ///   .//  this._coffin.state.st

        this._playerCharacter = new PlayerCharacter();
        this._playerCharacter.position.set(common.STAGE_WIDTH * 0.5, -80);
        this._playerHolder.addChild(this._playerCharacter);

        this._alphaFilter = new PIXI.filters.AlphaFilter(0.6);
        this._glowFilter = new GlowFilter({
            distance: 30,
            outerStrength: 1,
            innerStrength: 1,
            color: 0x7efd8f,
        });

        const spineKey = "ghost_" + P3.utils.padNum(common.ghostId, 2);

        this._ghostHolder = new PIXI.Container();
        this._playerHolder.addChild(this._ghostHolder);

        this._ghost = new DrawingSpine(P3.assets.spineData(spineKey));
        this._ghost.scale.set(0.5);
        this._ghost.autoUpdate = false;
        this._ghost.stateData.defaultMix = 0.2;
        this._ghost.update(0);
        this._ghost.position.set(this._playerCharacter.x - 400, this._playerCharacter.y - this._ghost.height * 0.5);
        this._ghost.state.setAnimation(0, "chase", true);
        this._ghost.visible = false;
        this._ghost.filters = [this._glowFilter, this._alphaFilter];
        this._ghostHolder.addChild(this._ghost);

        const hacks = common.saveData.getSpineHacks("ghost");
        for (let key in hacks) {
            if (hacks.hasOwnProperty(key)) {
                const t = hacks[key];
                const path = new paper.CompoundPath();
                path.importJSON(t.path);

                const slot = this._ghost.skeleton.findSlot(key);
                const sprite = slot.currentSprite ? slot.currentSprite : slot.currentMesh;// first try sprite, fallback to mesh
                const center = this.toLocal(sprite.position, sprite.parent);
                path.position.set(center.x + t.offset.x, center.y + t.offset.y);
                this._ghost.addTextureHack(DrawingTextureHack.create(path, slot, center));
            }
        }

        // create camera
        this._camera = new P3.NewCamera(
            new PIXI.Point(0, 0),
            new PIXI.Point(common.STAGE_WIDTH * 0.5, 0)
        );
        this._camera.zoom = 1;

        this._camera.addLayer(new P3.CameraLayer(this._tileHolder, new PIXI.Point(1, 0)));
        this._camera.addLayer(new P3.CameraLayer(this._floorHolder, new PIXI.Point(1, 0)));
        this._camera.addLayer(new P3.CameraLayer(this._wallEntityHolder, new PIXI.Point(1, 0)));
        this._camera.addLayer(new P3.CameraLayer(this._backHolder, new PIXI.Point(1, 0)));
        this._camera.addLayer(new P3.CameraLayer(this._playerHolder, new PIXI.Point(1, 0)));
        this._camera.addLayer(new P3.CameraLayer(this._frontHolder, new PIXI.Point(1, 0)));

        this._tileManager = new TileManager(this._tileHolder, [
            "bg_tile_1",
            "bg_tile_2"
        ]);
        this._tileManager.worldLength = this._worldLength - common.STAGE_WIDTH * 0.5;
        this._tileManager.populate(4);

        this._floorTileManager = new FloorTileManager(this._floorHolder, [
            "floor"
        ]);
        this._floorTileManager.worldLength = this._worldLength - common.STAGE_WIDTH * 0.5;
        this._floorTileManager.populate(4, 500);

        this._obstacleManager = new ObstacleManager(this._obstacleHolder);
        this._obstacleManager.worldLength = this._worldLength - common.STAGE_WIDTH * 0.5;

        this._entityManager = new EntityManager(this._wallEntityHolder);
        this._entityManager.worldLength = this._worldLength - common.STAGE_WIDTH * 0.5;

        this._cameraTarget = new PIXI.Point(this._playerCharacter.x, 0);
        this._camera.setTarget(this._cameraTarget);

        this._moveTarget = this._playerCharacter.position.clone();

        this._door = new InsideDoor();
        this._backHolder.addChild(this._door.back);
        this._backHolder.addChild(this._door.door);
        this._frontHolder.addChild(this._door.top);

        const offset = -80;
        this._door.back.x = this._worldLength - common.STAGE_WIDTH * 0.5 - offset;
        this._door.door.x = this._worldLength - common.STAGE_WIDTH * 0.5 - offset;
        this._door.top.x = this._worldLength - common.STAGE_WIDTH * 0.5 - offset;

        this._coffinShake = new Shake();
        this._coffinShake.maxOffsetX = 14;
        this._coffinShake.maxOffsetY = 14;
        this._coffinShake.traumaFade = 0;
        this._coffinShake.setTarget(this._coffin);

        this._emitterDustPuff = new Emitter(this._playerHolder, [P3.assets.texture("smoke")], P3.assets.json("emitter_dust_puff_00"));
        this._emitterDustPuff.autoUpdate = false;
        this._emitterDustPuff.updateOwnerPos(this._playerCharacter.x + 20, this._playerCharacter.y + 30);
        this._emitterDustPuff.emit = false;

        this._emitterDustPuff2 = new Emitter(this._playerHolder, [P3.assets.texture("smoke")], P3.assets.json("emitter_dust_puff_00"));
        this._emitterDustPuff2.autoUpdate = false;
        this._emitterDustPuff2.updateOwnerPos(this._playerCharacter.x + 70, this._playerCharacter.y - 20);
        this._emitterDustPuff2.emit = false;

        this._velma = new PIXI.AnimatedSprite([P3.assets.texture("velma_2"), P3.assets.texture("velma_1")]);
        this._velma.autoUpdate = false;
        this._velma.animationSpeed = 30;
        this._velma.loop = false;
        this._velma.gotoAndStop(0);
        this._velma.anchor.set(0.5, 1);
        this._velma.position.set(this._worldLength - 540, 40);
        this._playerHolder.addChild(this._velma);

        this._daphne = new PIXI.AnimatedSprite([P3.assets.texture("daphne_1"), P3.assets.texture("daphne_2")]);
        this._daphne.autoUpdate = false;
        this._daphne.loop = true;
        this._daphne.gotoAndPlay(0);
        this._daphne.anchor.set(0.5, 1);
        this._daphne.position.set(this._worldLength - 400, -90);
        this._playerHolder.addChildAt(this._daphne, 0);

        this._lookAhead = new PIXI.Point();
        this._state = null;

        this._ghostOffset = new PIXI.Point(this._playerCharacter.x - this._ghost.x, 0);
    }

    begin() {
        this.setState(World.STATE.INTRO);
    }

    update(dt) {

        this._tweens.update();
        this._coffinShake.update(dt);
        this._camera.update();
        this._velma.update(dt);
        this._daphne.update(dt);
        this._coffin.update(dt);
        this._ghost.update(dt);
        this._emitterDustPuff.update(dt);
        this._emitterDustPuff2.update(dt);
        this._playerCharacter.update(dt);

        if (this._running) {

            this._ghost.x = this._playerCharacter.x - this._ghostOffset.x;

            this._playerCharacter.x += this._playerCharacter.speed * dt;
            const ease = 3;
            this._moveTarget.y = P3.math.clamp(this._moveTarget.y, this._minHeight, this._maxHeight);
            this._playerCharacter.y += (this._moveTarget.y - this._playerCharacter.y) * ease * dt;
            this._playerCharacter.y = P3.math.clamp(this._playerCharacter.y, this._minHeight, this._maxHeight);

            this._cameraTarget.x = this._playerCharacter.x + this._lookAhead.x;
            this._tileManager.update();
            this._floorTileManager.update();

            if (this._playerCharacter.x >= this._worldLength - 400) {
                this._door.open();
            }
        }

        this._entityManager.update(this._playerCharacter.getGlobalPosition().x + 220, dt);
        this._obstacleManager.update(this._playerCharacter);

        this._stateUpdate(dt);
    }

    setState(state) {
        this._state = state;
        this._stateBegin();
    }

    _stateBegin() {
        switch (this._state) {
            case World.STATE.INTRO: {

                P3.utils.delayg(this._tweens, 0, _ => {
                    this._sfx = P3.audio.playSound("sfx_ghostly_background_0" + P3.math.randomInt(0, 2));
                    P3.audio.playSound("music_ghost_generic_coffin_loop_00");

                    P3.utils.delayg(this._tweens, 5, _ => {

                        this._sfx && this._sfx.stop();

                        this._coffin.state.setAnimation(0, "reveal_shake", false);
                        this._playerCharacter.revealShake();

                        this._entityManager._container.children[0]._view.texture = P3.assets.texture("portrait1_eyes_l");

                        P3.audio.playSound("music_ghost_a_reveal_sting_00");
                        this._coffinRattleSFX = P3.audio.playSound("sfx_coffin_rattle");

                        P3.utils.delayg(this._tweens, 3.3, _ => {
                            this._ghost.visible = true;
                            const startAlpha = this._alphaFilter.alpha;
                            this._alphaFilter.alpha = 0;
                            new P3.utils.TWEEN.Tween(this._alphaFilter, this._tweens)
                                .to({alpha: startAlpha}, 400)
                                .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
                                .start();

                            this._coffin.state.setAnimation(0, "opening2", false);

                            this._playerCharacter.openJump();

                            this._coffinRattleSFX.stop();
                            P3.audio.playSound("sfx_coffin_open");

                            P3.utils.delayg(this._tweens, 1, _ => {
                                this._playerCharacter.run();
                                this.setState(World.STATE.RUN);
                            });
                        });

                    })
                })

                /*
                new P3.utils.TWEEN.Tween(this._coffinShake, this._tweens)
                    .to({trauma: 0.5}, 5000)
                    .delay(600)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
                    .onStart(() => {
                        P3.audio.playMusic("music_ghost_generic_coffin_loop_00");

                        //P3.audio.stopMusic();
                        //P3.audio.playMusic(P3.utils.selectRandom(["music_ghost_a_reveal_sting_00", "music_ghost_b_reveal_sting_00"]));
                    })
                    .onComplete(() => {
                        this._coffinShake.traumaFade = 1;
                        this._coffinShake.trauma = 0;


                        this._coffin.state.setAnimation(0, "reveal_shake", false);

                        this._coffinRattleSFX = P3.audio.playSound("sfx_coffin_open", {loop: true});

                        this._playerCharacter.intro(() => {
                            // show ghost and start running
                            this._ghost.visible = true;
                            const startAlpha = this._alphaFilter.alpha;
                            this._alphaFilter.alpha = 0;
                            new P3.utils.TWEEN.Tween(this._alphaFilter, this._tweens)
                                .to({alpha: startAlpha}, 400)
                                .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
                                .start();

                            this._coffin.state.setAnimation(0, "opening2", false);

                            this._coffinRattleSFX.stop();
                            P3.audio.playSound("sfx_coffin_open");

                            this.setState(World.STATE.RUN);
                        });
                    })
                    .start();
*/
                break;
            }
            case World.STATE.RUN: {

                new P3.utils.TWEEN.Tween(this._ghostHolder, this._tweens)
                    .to({x: -100}, 1820)
                    .yoyo(true)
                    .repeat(Infinity)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                    .start();

                new P3.utils.TWEEN.Tween(this._ghostHolder, this._tweens)
                    .to({y: 50}, 1280)
                    .yoyo(true)
                    .repeat(Infinity)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                    .start();

                this._emitterDustPuff.emit = true;
                this._emitterDustPuff2.emit = true;
                this._running = true;

                this._playerCharacter.start();

                this.signals.began.dispatch();

                P3.audio.playMusic(P3.utils.selectRandom(["music_ghost_a_chase_loop_00", "music_ghost_a_chase_loop_01", "music_ghost_b_chase_loop_00"]));
                break;
            }
            case World.STATE.PHOTO: {
                new P3.utils.TWEEN.Tween(this._ghostOffset, this._tweens)
                    .to({x: 200}, 900)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                    .start();

                P3.utils.delayg(this._tweens, 1.2, () => {
                    this._velma.gotoAndPlay(0);
                })

                new P3.utils.TWEEN.Tween(this._lookAhead, this._tweens)
                    .to({x: 200}, 1400)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                    .onComplete(() => {

                        P3.audio.playSound("sfx_camera_flash_00");

                        // having to offset whole container and move back to frame photo properly, couldn't find another way
                        // todo - might be able to use optional transform param of render() to ovoid weird offset here
                        this.x -= 200;
                        const rt = PIXI.RenderTexture.create({
                            width: 1040 * 0.8,
                            height: 960 * 0.8
                        });
                        common.renderer.render(this, rt);
                        common.photoTexture = rt;
                        this.x += 200;

                        const flash = new PIXI.Sprite(PIXI.Texture.WHITE);
                        flash.blendMode = PIXI.BLEND_MODES.ADD;
                        flash.width = common.STAGE_WIDTH;
                        flash.height = common.STAGE_HEIGHT;
                        this.parent.addChild(flash);

                        flash.alpha = 0;

                        this._playerCharacter.stop();

                        new P3.utils.TWEEN.Tween(flash, this._tweens)
                            .to({alpha: 1}, 200)
                            .yoyo(true)
                            .repeat(1)
                            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                            .onComplete(() => {
                                flash.destroy();

                            })
                            .start();

                        this._running = false;
                        this.signals.completed.dispatch();

                    })
                    .start();

                break;
            }
        }
    }

    _stateUpdate(dt) {
        switch (this._state) {
            case World.STATE.INTRO: {
                break;
            }
            case World.STATE.RUN: {
                if (this._playerCharacter.x >= this._worldLength - 2000) {
                    this.setState(World.STATE.PHOTO);
                }
                break;
            }
            case World.STATE.PHOTO: {
                break;
            }
        }
    }

}

World.STATE = {};
World.STATE.INTRO = "intro";
World.STATE.RUN = "run";
World.STATE.PHOTO = "photo";